import React, { useState, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Stack,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { MyLocation, Check, ArrowBack } from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import drawdownService from "../../../../../services/drawdownService";
import { processAutofillData } from "../utils/autofillUtils";
import { validateStep as validateStepUtil } from "../utils/validationUtils";
import {
  DT_URL_PARAMS,
  DRAWDOWN_TOOL_EVENTS,
  WIZARD_STEPS,
  CACHE_TIMES,
  ERROR_MESSAGES,
  DEFAULT_FORM_STATE,
} from "../constants/drawdownConstants";

const steps = WIZARD_STEPS;

const NewRunWizard = ({
  onRunComplete,
  isMapToolEnabled,
  onMapToolToggle,
  autofillData,
}) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [coordinatesCaptured, setCoordinatesCaptured] = useState(false);

  // Form data state - using correct backend field names
  const [formData, setFormData] = useState(DEFAULT_FORM_STATE);

  // Validation state
  const [errors, setErrors] = useState({});

  // Fetch aquifers and units using useQuery
  const { data: aquifers = [], isLoading: aquifersLoading } = useQuery(
    "drawdown-aquifers",
    () => drawdownService.getAquifers(getAccessTokenSilently, isAuthenticated),
    {
      enabled: isAuthenticated,
      staleTime: CACHE_TIMES.AQUIFERS_UNITS,
      refetchOnWindowFocus: false,
    }
  );

  const { data: units = [], isLoading: unitsLoading } = useQuery(
    "drawdown-units",
    () => drawdownService.getUnits(getAccessTokenSilently, isAuthenticated),
    {
      enabled: isAuthenticated,
      staleTime: CACHE_TIMES.AQUIFERS_UNITS,
      refetchOnWindowFocus: false,
    }
  );

  const loading = aquifersLoading || unitsLoading;

  // Handle autofill data on mount
  useEffect(() => {
    const processedData = processAutofillData(autofillData, aquifers, units);
    if (processedData) {
      setFormData(processedData);
      setCoordinatesCaptured(true);
    }
  }, [autofillData, aquifers, units]);

  // Submit run mutation
  const submitRunMutation = useMutation(
    (data) =>
      drawdownService.submitRun(data, getAccessTokenSilently, isAuthenticated),
    {
      onSuccess: (response) => {
        // Invalidate and refetch runs list
        void queryClient.invalidateQueries("drawdown-runs");

        if (response && response.run_ndx) {
          onRunComplete(response.run_ndx);
        } else {
          setErrors({ submit: ERROR_MESSAGES.NO_RUN_RESULTS });
        }
      },
      onError: (error) => {
        setErrors({ submit: ERROR_MESSAGES.SUBMIT_FAILED });
        console.error(error);
      },
    }
  );

  // Real coordinate capture from URL params - listen for changes
  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const lat = urlParams.get(DT_URL_PARAMS.LAT);
      const lon = urlParams.get(DT_URL_PARAMS.LON);

      if (lat && lon) {
        setFormData((prev) => ({
          ...prev,
          latitude_dd: parseFloat(lat).toFixed(6),
          longitude_dd: parseFloat(lon).toFixed(6),
        }));

        setCoordinatesCaptured(true);

        // Clear any validation errors since map coordinates are always valid
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.latitude_dd;
          delete newErrors.longitude_dd;
          delete newErrors.coordinates;
          return newErrors;
        });

        // Clear URL parameters after capture
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.delete(DT_URL_PARAMS.LAT);
        currentParams.delete(DT_URL_PARAMS.LON);
        const newSearch = currentParams.toString();
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`
        );
      }
    };

    // Listen for URL parameter changes
    window.addEventListener("popstate", handleUrlChange);
    window.addEventListener(
      DRAWDOWN_TOOL_EVENTS.URL_PARAMS_CHANGED,
      handleUrlChange
    );

    // Check on mount
    handleUrlChange();

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
      window.removeEventListener(
        DRAWDOWN_TOOL_EVENTS.URL_PARAMS_CHANGED,
        handleUrlChange
      );
    };
  }, []);

  const handleMapToolToggle = () => {
    // Clear coordinates when activating map tool
    if (!isMapToolEnabled) {
      setFormData((prev) => ({
        ...prev,
        latitude_dd: "",
        longitude_dd: "",
      }));
      setCoordinatesCaptured(false);
    }
    onMapToolToggle();
  };

  const validateStep = (step) => {
    const newErrors = validateStepUtil(step, formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleRunProjection = () => {
    // Clear any previous submit errors
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.submit;
      return newErrors;
    });

    const payload = { ...formData };

    if (!payload.use_custom_t) delete payload.custom_transmissivity;
    if (!payload.use_custom_s) delete payload.custom_storativity;

    submitRunMutation.mutate(payload);
  };

  const renderLocationStep = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9rem", mb: 1, color: "text.secondary" }}
      >
        Where is the proposed well going to be located?
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Latitude"
          type="number"
          value={formData.latitude_dd}
          onChange={(e) => handleInputChange("latitude_dd", e.target.value)}
          error={!!errors.latitude_dd || !!errors.coordinates}
          helperText={errors.latitude_dd || errors.coordinates}
          disabled={isMapToolEnabled}
          size="small"
          fullWidth
        />

        <TextField
          label="Longitude"
          type="number"
          value={formData.longitude_dd}
          onChange={(e) => handleInputChange("longitude_dd", e.target.value)}
          error={!!errors.longitude_dd || !!errors.coordinates}
          helperText={errors.longitude_dd}
          disabled={isMapToolEnabled}
          size="small"
          fullWidth
        />

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          — OR —
        </Typography>

        <Button
          variant={isMapToolEnabled ? "contained" : "outlined"}
          color={isMapToolEnabled ? "secondary" : "primary"}
          onClick={handleMapToolToggle}
          startIcon={<MyLocation />}
          fullWidth
          sx={{
            "& .MuiButton-startIcon": {
              mr: { xs: 0, sm: 1 },
            },
            "& .MuiButton-label": {
              display: { xs: "none", sm: "block" },
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: { xs: "none", sm: "inline" },
            }}
          >
            {isMapToolEnabled
              ? "Tool Active - Click Map"
              : "Select Location on Map"}
          </Box>
          <Box
            component="span"
            sx={{
              display: { xs: "inline", sm: "none" },
            }}
          >
            {isMapToolEnabled ? "Active" : "Select"}
          </Box>
        </Button>

        {isMapToolEnabled && (
          <Alert severity="success">
            <Typography variant="body2">
              Tool is active! Click anywhere on the map to capture coordinates.
            </Typography>
          </Alert>
        )}

        {coordinatesCaptured && !isMapToolEnabled && (
          <Alert severity="success">
            <Typography variant="body2">
              ✓ Coordinates captured successfully! Click "Next" to continue.
            </Typography>
          </Alert>
        )}
      </Stack>
    </Box>
  );

  const renderWellDetailsStep = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9rem", mb: 1, color: "text.secondary" }}
      >
        Enter the details for your proposed well
      </Typography>

      <Stack spacing={2}>
        <TextField
          label="Proposed Well ID*"
          value={formData.proposed_well_name}
          onChange={(e) =>
            handleInputChange("proposed_well_name", e.target.value)
          }
          error={!!errors.proposed_well_name}
          helperText={errors.proposed_well_name}
          size="small"
          fullWidth
        />

        <FormControl size="small" fullWidth error={!!errors.aquifer_ndx}>
          <InputLabel>Source Aquifer*</InputLabel>
          <Select
            value={formData.aquifer_ndx}
            onChange={(e) => handleInputChange("aquifer_ndx", e.target.value)}
            label="Source Aquifer*"
            disabled={loading}
            variant="outlined"
          >
            {aquifers.map((aquifer) => (
              <MenuItem key={aquifer.aquifer_ndx} value={aquifer.aquifer_ndx}>
                {aquifer.aquifer_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Maximum Search Radius*"
          type="number"
          value={formData.distance_radius}
          onChange={(e) => handleInputChange("distance_radius", e.target.value)}
          error={!!errors.distance_radius}
          helperText={errors.distance_radius}
          size="small"
          fullWidth
        />

        <FormControl size="small" fullWidth error={!!errors.distance_units}>
          <InputLabel>Search Radius Units*</InputLabel>
          <Select
            value={formData.distance_units}
            onChange={(e) =>
              handleInputChange("distance_units", e.target.value)
            }
            label="Units*"
            disabled={loading}
            variant="outlined"
          >
            {units.map((unit) => (
              <MenuItem key={unit.unit_ndx} value={unit.unit_ndx}>
                {unit.remark}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Annual Production Requested* (AF)"
          type="number"
          value={formData.proposed_annual_af}
          onChange={(e) =>
            handleInputChange("proposed_annual_af", e.target.value)
          }
          error={!!errors.proposed_annual_af}
          helperText={errors.proposed_annual_af}
          size="small"
          fullWidth
        />

        <TextField
          label="Discharge Rate* (GPM)"
          type="number"
          value={formData.proposed_rate_gpm}
          onChange={(e) =>
            handleInputChange("proposed_rate_gpm", e.target.value)
          }
          error={!!errors.proposed_rate_gpm}
          helperText={errors.proposed_rate_gpm}
          size="small"
          fullWidth
        />
      </Stack>
    </Box>
  );

  const renderCustomInputsStep = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9rem", mb: 1, color: "text.secondary" }}
      >
        Override default transmissivity and storativity values (optional)
      </Typography>

      <Stack spacing={2}>
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.use_custom_t}
                onChange={(e) =>
                  handleInputChange("use_custom_t", e.target.checked)
                }
                size="small"
              />
            }
            label="Use Custom Transmissivity"
          />

          <TextField
            label="Custom Transmissivity"
            type="number"
            value={formData.custom_transmissivity}
            onChange={(e) =>
              handleInputChange("custom_transmissivity", e.target.value)
            }
            error={!!errors.custom_transmissivity}
            helperText={errors.custom_transmissivity}
            disabled={!formData.use_custom_t}
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            placeholder="Enter transmissivity value"
          />
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.use_custom_s}
                onChange={(e) =>
                  handleInputChange("use_custom_s", e.target.checked)
                }
                size="small"
              />
            }
            label="Use Custom Storativity"
          />

          <TextField
            label="Custom Storativity"
            type="number"
            value={formData.custom_storativity}
            onChange={(e) =>
              handleInputChange("custom_storativity", e.target.value)
            }
            error={!!errors.custom_storativity}
            helperText={errors.custom_storativity}
            disabled={!formData.use_custom_s}
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            placeholder="Enter storativity value"
          />
        </Box>
      </Stack>
    </Box>
  );

  const renderNotesStep = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9rem", mb: 1, color: "text.secondary" }}
      >
        Add any additional notes or comments (optional)
      </Typography>

      <TextField
        label="Notes"
        multiline
        rows={3}
        value={formData.run_notes}
        onChange={(e) => handleInputChange("run_notes", e.target.value)}
        placeholder="Enter any additional notes or comments about this analysis..."
        size="small"
        fullWidth
      />
    </Box>
  );

  const renderReviewStep = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography
        variant="body1"
        sx={{ fontSize: "0.9rem", mb: 1, color: "text.secondary" }}
      >
        Review your settings and run the analysis
      </Typography>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Box>
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ fontWeight: 600 }}
            >
              Location
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Latitude:
              </Typography>
              <Typography variant="body2">{formData.latitude_dd}</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Longitude:
              </Typography>
              <Typography variant="body2">{formData.longitude_dd}</Typography>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ fontWeight: 600 }}
            >
              Well Details
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                ID:
              </Typography>
              <Typography variant="body2">
                {formData.proposed_well_name}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Aquifer:
              </Typography>
              <Typography variant="body2">
                {aquifers.find((a) => a.aquifer_ndx === formData.aquifer_ndx)
                  ?.aquifer_name || "-"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Search Radius:
              </Typography>
              <Typography variant="body2">
                {formData.distance_radius}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Units:
              </Typography>
              <Typography variant="body2">
                {units.find((u) => u.unit_ndx === formData.distance_units)
                  ?.remark || "-"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Annual Production:
              </Typography>
              <Typography variant="body2">
                {formData.proposed_annual_af} AF
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: "text.secondary" }}
              >
                Discharge Rate:
              </Typography>
              <Typography variant="body2">
                {formData.proposed_rate_gpm} GPM
              </Typography>
            </Box>
          </Box>

          {(formData.use_custom_t || formData.use_custom_s) && (
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                Custom Inputs
              </Typography>
              {formData.use_custom_t && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "text.secondary" }}
                  >
                    Custom Transmissivity:
                  </Typography>
                  <Typography variant="body2">
                    {formData.custom_transmissivity}
                  </Typography>
                </Box>
              )}
              {formData.use_custom_s && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "text.secondary" }}
                  >
                    Custom Storativity:
                  </Typography>
                  <Typography variant="body2">
                    {formData.custom_storativity}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {formData.run_notes && (
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                Notes
              </Typography>
              <Typography variant="body2">{formData.run_notes}</Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      <Box display="flex" alignItems="center" gap={1}>
        <Button
          onClick={handleBack}
          size="large"
          variant="outlined"
          sx={{ minWidth: "auto" }}
          startIcon={<ArrowBack />}
        ></Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleRunProjection}
          size="large"
          fullWidth
          startIcon={<Check />}
          disabled={submitRunMutation.isLoading}
        >
          {submitRunMutation.isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Submitting...
            </>
          ) : (
            "Run Analysis"
          )}
        </Button>
      </Box>

      {errors.submit && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.submit}
        </Alert>
      )}
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderLocationStep();
      case 1:
        return renderWellDetailsStep();
      case 2:
        return renderCustomInputsStep();
      case 3:
        return renderNotesStep();
      case 4:
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Compact horizontal stepper */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontSize: "0.95rem", fontWeight: 600 }}
          >
            {steps[activeStep]}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontSize: "0.8rem", color: "text.secondary" }}
          >
            {activeStep + 1} of {steps.length}
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={((activeStep + 1) / steps.length) * 100}
          sx={{ mb: 2, height: 6, borderRadius: 3 }}
        />

        <Stepper
          activeStep={activeStep}
          sx={{
            mb: 0,
            "& .MuiStepConnector-root": { flex: "0 1 20px" },
            "& .MuiStepLabel-iconContainer": { padding: 0 },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ "& .MuiStepLabel-label": { display: "none" } }}>
                {/* No label content */}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Step content */}
      <Box sx={{ mb: 3 }}>{renderStepContent(activeStep)}</Box>

      {/* Navigation buttons */}
      {activeStep < steps.length - 1 && (
        <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            size="small"
            variant="outlined"
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            size="small"
            sx={{ minWidth: 80 }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default NewRunWizard;
